// structuralForm.js
// Builds the fill instructions for the structural jurisdiction form from:
//   project (intake), details (structural Project Details), slots + siteSystems
//   (Report Editor), the chosen engineer/firm profile, and the photo list.
// The Cloud Function applies the instructions to the template; this file holds
// all the mapping so it can be tested against the template field list.
//
// Instruction types:
//   { t: 'text',  f: 's4a', v }      — a field, or rows s4a_1, s4a_2 … (wrapped by the function)
//   { t: 'lines', f: [..names], v }  — wrap across an explicit list of fields
//   { t: 'select', f, v }            — dropdown
//   { t: 'check',  f, v: true }      — checkbox
//   { t: 'radio',  f, v: 'Choice2' } — radio group

import FIELDS from './structuralFields.json';
import { titleName, ownerOf } from './projectFields';
import {
  structuralForm, getStructuralSections, effectiveAnswers, sectionIsNA,
  numberStructuralPhotos, NA_TEXT,
} from './structuralItems';

// ─── Templates ────────────────────────────────────────────────────────────────

export const structuralTemplateKey = (county) => {
  const c = (county || '').toLowerCase();
  if (c.includes('boca')) return 'boca_raton';
  if (c.includes('city of miami')) return 'city_of_miami';
  if (c.includes('miami')) return 'miami_dade';
  return 'broward';
};

// ─── Standard texts (switch with the repairs until the engineer edits them) ───

export const OCC_NO_REPAIRS   = "The building's condition is consistent with its age and is structurally safe for continued use under its present occupancy.";
export const OCC_WITH_REPAIRS = "The building's condition is consistent with its age and is structurally safe for continued use under its present occupancy while the repair items identified herein are performed. Client or contractor shall apply for all necessary permits.";
const DET_BASE = "The building's condition is consistent with its age. No substantial structural deterioration observed.";

export const repairList = (slots, sections, siteSystems) => {
  const items = [];
  sections.forEach(sec => {
    if (sectionIsNA(sec, slots?.[sec.key], siteSystems)) return;
    (slots?.[sec.key]?.repairs || []).forEach(r => { if ((r.text || '').trim()) items.push(r.text.trim()); });
  });
  return items;
};
export const numberedList = (items) => items.map((t, i) => `${i + 1}) ${t.replace(/\.?$/, '.')}`).join(' ');

// Defaults for every Project Details field. `std` marks text that follows the
// repairs until edited (stored value empty or equal to one of its standard forms).
export const detailDefaults = ({ project, repairs, condition }) => {
  const has = repairs.length > 0;
  const list = numberedList(repairs);
  const substantial = condition === 'substantial';
  return {
    reportType:         'initial',
    amendedPermit:      '',
    caseNumber:         project?.permitNumber || '',
    jurisdictionName:   project?.county === 'Miami-Dade' ? 'Miami-Dade County' : (project?.county || ''),
    noticeDate:         project?.noticeDate || '',
    inspectStart:       '',
    inspectEnd:         '',
    nameOnTitle:        titleName(project),
    condoAssoc:         project?.condoAssoc || 'N/A',
    occupancyClass:     project?.occupancy || '',
    presentUse:         project?.presentUse || '',
    generalDescription: project?.generalDescription || '',
    specialFeatures:    project?.specialFeatures || '',
    additions:          'None observed.',
    additionalComments: 'N/A',
    coastDistance:      '',
    footprintArea:      '',
    legalAttached:      true,
    aerialAttached:     true,
    testing:            'N/A',
    occupancyOk:        'Yes',
    occupancyExplanation: has ? OCC_WITH_REPAIRS : OCC_NO_REPAIRS,
    repairsDescription: has ? list : 'N/A',
    vacate:             'No',
    recordResearched:   'Yes',
    recordComments:     'None found.',
    sheets:             '0',
    drawings:           '0',
    tests:              '0',
    condition:          has ? '' : 'passed',
    unpermitted:        false,
    det15a:             has ? `${DET_BASE} Repairs required: ${list}` : DET_BASE,
    det16:              substantial ? '' : 'N/A',
    det17:              substantial ? '' : 'N/A',
    det18:              'Visual inspection.',
    det19:              has ? 'Repairs to be completed within 180 days.' : 'N/A',
    det20:              'No unsafe or dangerous conditions observed.',
  };
};

// Keys whose default follows the repairs / condition. Each follows until the
// engineer edits it; "Reset to standard" deletes the stored value.
export const FOLLOWING_KEYS = ['occupancyExplanation', 'repairsDescription', 'det15a', 'det16', 'det17', 'det19', 'condition'];

// Value shown and printed: the stored edit if there is one, else the default.
export const resolveDetails = (stored, ctx) => {
  const out = { ...detailDefaults(ctx) };
  Object.entries(stored || {}).forEach(([k, v]) => { if (v !== undefined && v !== null) out[k] = v; });
  return out;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fieldsFor = (key) => FIELDS[key] || {};
const exists = (key, name) => !!fieldsFor(key)[name] || !!fieldsFor(key)[`${name}_1`];
const choiceOf = (options, value) => {
  const i = options.findIndex(o => (typeof o === 'string' ? o : o.v) === value);
  return i >= 0 ? `Choice${i + 1}` : null;
};
const fullAddress = (p) => [p?.address, p?.city, [p?.state || 'FL', p?.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
const mdy = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : iso;
};

// ─── Build ────────────────────────────────────────────────────────────────────

export const buildStructuralFill = ({ project, details, slots, siteSystems, profile, photos }) => {
  const county   = project?.county;
  const key      = structuralTemplateKey(county);
  const form     = structuralForm(county);       // 'miami' | 'boca' | 'broward'
  const sections = getStructuralSections(county);
  const repairs  = repairList(slots, sections, siteSystems);
  // Section 1 facts and the notice date come from the project (New Project / Edit
  // Project), not from values once stored on Project Details.
  const d        = {
    ...resolveDetails(details, { project, repairs, condition: details?.condition }),
    nameOnTitle:        titleName(project),
    condoAssoc:         project?.condoAssoc || 'N/A',
    occupancyClass:     project?.occupancy || '',
    presentUse:         project?.presentUse || '',
    generalDescription: project?.generalDescription || '',
    specialFeatures:    project?.specialFeatures || '',
    noticeDate:         project?.noticeDate || '',
  };
  const owner    = ownerOf(project);
  const ops      = [];
  const missing  = [];

  const put = (t, f, v) => {
    if (v === undefined || v === null || v === '' || v === false) return;
    const names = Array.isArray(f) ? f : [f];
    if (!names.every(n => exists(key, n))) { missing.push(...names.filter(n => !exists(key, n))); return; }
    ops.push({ t, f, v });
  };
  const text   = (f, v) => put(Array.isArray(f) ? 'lines' : 'text', f, v);
  const check  = (f, on = true) => { if (on) put('check', f, true); };
  const select = (f, v) => put('select', f, v);
  const radio  = (f, v) => put('radio', f, v);
  const mark   = (f, on = true) => { if (on) text(f, 'X'); };   // Boca: X in a text blank

  // Threshold: Report Editor checklist if answered, otherwise the project's Section 1 answer
  const threshold = typeof siteSystems?.threshold?.present === 'boolean'
    ? siteSystems.threshold.present
    : project?.thresholdBuilding === 'Yes';
  const photoCount = numberStructuralPhotos(county, slots, siteSystems).filter(c => c.photoId).length;
  const engLine = [profile?.engineerName, profile?.licenseNumber ? `(${profile.licenseNumber})` : ''].filter(Boolean).join(' ');
  // Qualifications as typed in Settings (line breaks kept); name and license if blank
  const qualText = (profile?.qualifications || '').trim() || engLine;
  const hasRepairs = repairs.length > 0;
  const inspectDates = d.inspectEnd && d.inspectEnd !== d.inspectStart ? `${mdy(d.inspectStart)} – ${mdy(d.inspectEnd)}` : mdy(d.inspectStart);

  // ── Front page and description ───────────────────────────────────────────
  if (form === 'miami') {
    radio('report_type', d.reportType === 'amended' ? 'Choice2' : 'Choice1');
    radio('responsibility', 'Choice1');
    radio('dp_type', 'Choice1');
    const cond = { substantial: 'Choice1', dangerous: 'Choice2', immediate: 'Choice3', maintenance: 'Choice4', passed: 'Choice5' }[d.condition];
    if (cond) radio('condition', cond);
    check('unpermitted_work', !!d.unpermitted);
    text('firm_name', profile?.firmName); text('firm_address', profile?.address);
    text('firm_phone', profile?.phone);   text('firm_email', profile?.email);
    text('inspect_start', mdy(d.inspectStart)); text('inspect_end', mdy(d.inspectEnd || d.inspectStart));
    text('eng_name', profile?.engineerName); text('eng_license', profile?.licenseNumber);
    text('case_number', d.caseNumber || 'N/A'); text('jurisdiction_name', d.jurisdictionName);
    text('s1a', d.nameOnTitle); text('s1b', fullAddress(project)); text('s1b_bldg', project?.buildingNumber);
    text('s1c', project?.legalDesc); check('s1c_attached', d.legalAttached);
    text('s1d', owner); text('s1e', project?.ownerMailAddr); text('s1f', project?.ownerEmail || 'N/A');
    text('s1g', project?.ownerPhone || 'N/A'); text('s1h', project?.folio); text('s1i', d.condoAssoc);
    text('s1j', d.occupancyClass); text('s1k', d.presentUse); text('s1l', d.generalDescription);
    text('s1m', project?.stories); select('s1n', threshold ? 'Yes' : 'No'); text('s1o', d.additions);
    check('s1p_attached', d.aerialAttached); text('s1q', d.coastDistance);
    text('s1r', project?.sqFt); text('s1s', d.footprintArea);
    // 2. Inspections
    text('s2a', mdy(d.noticeDate) || 'N/A'); text('s2b', inspectDates);
    text('s2c', qualText); text('s2c1', profile?.discipline);
    if ((d.testing || '').trim().toUpperCase() === 'N/A') check('s2d_na'); else text('s2d', d.testing);
    select('s2e', hasRepairs ? 'Yes' : 'No'); text('s2e1', d.repairsDescription);
    select('s2f', d.occupancyOk); text('s2f1', d.occupancyExplanation);
    select('s2g', d.vacate); select('s2h', d.recordResearched); text('s2h1', d.recordComments);
    // 3. Supporting data
    text('s3a', d.sheets); text('s3b', String(photoCount)); text('s3c', d.drawings); text('s3d', d.tests);
    // 15–20
    text('s15a', d.det15a);
    check('s15b_na', d.condition !== 'substantial');
    text('s16', d.det16); text('s17', d.det17); text('s18', d.det18); text('s19', d.det19); text('s20', d.det20);
  }

  if (form === 'boca') {
    check(d.reportType === 'amended' ? 'report_amended' : 'report_initial');
    text('firm_name', profile?.firmName); text('firm_address', profile?.address); text('firm_phone', profile?.phone);
    text('inspect_start', mdy(d.inspectStart)); text('inspect_end', mdy(d.inspectEnd || d.inspectStart));
    mark(hasRepairs ? 'repairs_required' : 'repairs_none');
    text('eng_name', profile?.engineerName); text('eng_license', profile?.licenseNumber);
    const cond = { substantial: 'condition_substantial', dangerous: 'condition_dangerous', immediate: 'condition_immediate', maintenance: 'condition_maintenance', passed: 'condition_passed' }[d.condition];
    if (cond) check(cond);
    if (d.reportType === 'amended') { check('condition_amended'); text('amended_permit', d.amendedPermit); }
    text('s1a', d.nameOnTitle); text('s1b', fullAddress(project)); text('s1c', project?.legalDesc);
    text('s1d', owner); text('s1e', project?.ownerMailAddr); text('s1f', project?.folio);
    text('s1g', d.occupancyClass); text('s1h', threshold ? 'Yes' : 'No');
    text('s1i_use', d.presentUse); text('s1i_sqft', project?.sqFt); text('s1i_stories', project?.stories);
    text('s1j', d.generalDescription); text('s1k', d.specialFeatures || 'N/A'); text('s1l', d.additions);
    text('s2a', mdy(d.noticeDate) || 'N/A'); text('s2b', inspectDates); text('s2c', qualText); text('s2d', d.testing);
    mark(hasRepairs ? 's2e2' : 's2e1');
    text('s2e3', d.occupancyOk); text('s2e3_txt', d.occupancyExplanation);
    mark(d.recordResearched === 'Yes' ? 's2f_yes' : 's2f_no'); text('s2f1', d.recordComments);
    text('s3a', d.sheets); text('s3b', String(photoCount)); text('s3c', d.drawings); text('s3d', d.tests);
    text('s15a', d.det15a);
    if (d.condition !== 'substantial') check('s15b_na');
    text('s15b1', hasRepairs ? d.repairsDescription : d.det16);
    text('s15b2', d.det17); text('s15b3', d.det18); text('s15b4', d.det19); text('s15b5', d.det20);
  }

  if (form === 'broward') {
    text('firm_name', profile?.firmName); text('firm_address', profile?.address); text('firm_phone', profile?.phone);
    text('inspect_start', mdy(d.inspectStart)); text('inspect_end', mdy(d.inspectEnd || d.inspectStart));
    check(hasRepairs ? 'repairs_required' : 'repairs_none');
    check('dp_engineer');
    text('eng_name', profile?.engineerName); text('eng_license', profile?.licenseNumber);
    text('s1a', d.nameOnTitle); text('s1b', fullAddress(project)); text('s1c', project?.legalDesc);
    text('s1d', owner); text('s1e', project?.ownerMailAddr);
    text('s1f_email', project?.ownerEmail || 'N/A'); text('s1f_phone', project?.ownerPhone || 'N/A');
    text('s1g', project?.folio); text('s1h', d.occupancyClass); text('s1i', d.presentUse);
    text('s1j', d.generalDescription); text('s1j_construction', project?.constructionType);
    text('s1k_sqft', project?.sqFt); text('s1k_stories', project?.stories);
    check(threshold ? 's1l_yes' : 's1l_no');
    text('s1m', d.specialFeatures || 'N/A'); text('s1n', d.additions); text('s1o', d.additionalComments);
    text('s3a', mdy(d.noticeDate) || 'N/A'); text('s3b', inspectDates); text('s3c', qualText); text('s3d', d.testing);
    text('s3e', hasRepairs ? `${d.occupancyExplanation} Repairs: ${d.repairsDescription}` : `None required. ${d.occupancyExplanation}`);
    check(d.recordResearched === 'Yes' ? 's3f_yes' : 's3f_no'); text('s3f1', d.recordComments);
    text('s4a', d.sheets === '0' ? 'N/A' : d.sheets); text('s4b', `See attached (${photoCount}).`);
    text('s4c', d.drawings === '0' ? 'N/A' : d.drawings); text('s4d', d.tests === '0' ? 'N/A' : d.tests);
  }

  // ── Report Editor sections ───────────────────────────────────────────────
  sections.forEach(sec => {
    const slot = slots?.[sec.key];
    const na = sectionIsNA(sec, slot, siteSystems);
    const naBox = `s${sec.num}_na`;
    if (na) {
      if (exists(key, naBox)) check(naBox);
      else {
        // No N/A box on this form: print N/A on each text line
        sec.lines.forEach(l => { const f = `s${l.id}`; if (exists(key, f) && fieldsFor(key)[f]?.type !== 'Btn') text(f, NA_TEXT); });
      }
      return;
    }
    const { answers } = effectiveAnswers(sec, slot, siteSystems);
    sec.lines.forEach(line => fillLine({ key, form, line, answers, text, select, check, radio, mark }));
  });

  return { templateKey: key, ops, missing: [...new Set(missing)] };
};

// ─── One Report Editor line → fields ──────────────────────────────────────────

const fillLine = ({ key, form, line, answers, text, select, check, radio, mark }) => {
  const id = line.id;
  const v = answers[id];
  const txt = answers[`${id}_txt`];
  const f = (n) => (exists(key, n) ? n : null);
  if (v === undefined || v === '') return;

  // Line-level N/A box (stairs, ramps, guardrails, elevator …)
  if (v === NA_TEXT && f(`s${id}_na`)) { check(`s${id}_na`); return; }

  if (form === 'miami') {
    // City of Miami: balcony location is a dropdown; the location text goes in the 3rd row
    if (key === 'city_of_miami' && id === '7b2_location') { text('s7b2_3', v); return; }
    if (key === 'city_of_miami' && id === '7b2') {
      if (v === NA_TEXT) { text('s7b2_1', NA_TEXT); return; }
      select('s7b2_cond', v); if (txt) text(['s7b2_1', 's7b2_2'], txt); return;
    }
    if (line.kind === 'rating') {
      const textField = id === '7a1' ? 's7a1_cond_txt' : `s${id}`;
      if (v === NA_TEXT && !line.options.includes('N/A')) { text(textField, NA_TEXT); return; }   // no N/A in the dropdown
      select(f(`s${id}_cond`) || `s${id}`, v);
      if (txt) text(textField, txt);
      return;
    }
    if (line.kind === 'select') {
      select(f(`s${id}_cond`) || `s${id}`, v);
      if (txt) text(f(`s${id}_other`) || `s${id}`, txt);
      return;
    }
    if (line.kind === 'yesno') {
      if (id === '9e') { if (v === 'No') check('s9e1'); return; }
      select(`s${id}`, v);
      if (txt) text(f(`s${id}_txt`) || `s${id}`, txt);
      return;
    }
    if (line.kind === 'radio') {
      const i = line.options.findIndex(o => o.v === v);
      if (id === '6k') {
        radio('s6k', choiceOf(line.options, v));
        if (i > 0 && txt) text(`s6k${i + 1}`, txt);
        return;
      }
      if (id === '9b') { radio('s9b', choiceOf(line.options, v)); return; }
      if (id === '9d') {
        if (i === 0) check('s9d1');
        [2, 3, 4].forEach(n => { if (n === i + 1) text(`s9d${n}`, txt || v); else check(`s9d${n}_na`); });
        return;
      }
      return;
    }
    text(`s${id}`, v);
    return;
  }

  if (form === 'boca') {
    if (line.kind === 'rating') { text(`s${id}`, txt ? `${v}. ${txt}` : v); return; }
    if (line.kind === 'yesno') {
      if (id === '6l') { text('s6l', v); if (v === 'No') mark('s6l1'); return; }
      if (id === '9e') { text('s9e', v); if (v === 'No') mark('s9e1'); return; }
      text(`s${id}`, txt ? `${v}. ${txt}` : v); return;
    }
    if (line.kind === 'radio') {
      const i = line.options.findIndex(o => o.v === v);
      if (id === '6k') { mark(`s6k${i + 1}`); return; }
      if (id === '9b') { text(`s9b${i + 1}`, i === 2 && txt ? `X — ${txt}` : 'X'); return; }
      if (id === '9d') {
        if (i === 0) mark('s9d1');
        [2, 3, 4].forEach(n => { if (n === i + 1) text(`s9d${n}`, txt || 'X'); else check(`s9d${n}_na`); });
        return;
      }
      return;
    }
    if (id === '9c') { text(['s9c', 's9c_cont'], v); return; }
    if (id === '9g') { text(['s9g', 's9g_cont_1', 's9g_cont_2'], v); return; }
    text(`s${id}`, v);
    return;
  }

  // Broward
  if (line.kind === 'rating') {
    const box = `s${id}_${v.toLowerCase()}`;
    if (f(box)) check(box);
    if (txt && f(`s${id}`)) text(`s${id}`, txt);
    return;
  }
  if (line.kind === 'yesno') {
    if (id === '5c') { check(v === 'Yes' ? 's5c_yes' : 's5c_no'); return; }
    if (id === '10b') { check(v === 'Yes' ? 's10b_yes' : 's10b_no'); return; }
    if (id === '6k') { check(v === 'Yes' ? 's6k2' : 's6k1'); return; }
    if (id === '9e') { check(v === 'Yes' ? 's9e2' : 's9e1'); return; }
    return;
  }
  if (line.kind === 'radio') {
    const i = line.options.findIndex(o => o.v === v);
    if (id === '6j') { check(`s6j${i + 1}`); if (i === 3 && txt) text('s6j4a', txt); return; }
    if (id === '9b') { check(i === 1 ? 's9b_significant' : 's9b_not_significant'); return; }
    if (id === '9d') { check(`s9d${i + 1}`); if (i === 3 && txt) text('s9d4a', txt); return; }
    return;
  }
  text(`s${id}`, v);
};
